import { Paragraphs, styling as paragraphStyling } from '@lib/components/CRichTextEditor/extensions/paragraphExtended'
import { styling as headingStyling } from '@lib/components/CRichTextEditor/extensions/headingExtended'
import {
  GOTENBERG_DIMENSION_PATTERN,
  DIMENSION_TO_PIXEL_CONVERSION,
  type GotenbergDimensionUnit,
} from '@lib/constants/dimensions.const'

const paragraphTypeToVarName = {
  [Paragraphs.BIG_TEXT]: 'big_text',
  [Paragraphs.NORMAL_TEXT]: 'normal_text',
  [Paragraphs.SMALL_TEXT]: 'small_text',
}

export function replaceThemePresetsWithInlineStyles(htmlContent: string, shouldPreserveStyles = false): string {
  if (!htmlContent) return htmlContent

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent

  const isInDocument = document.body.contains(tempDiv)
  if (!isInDocument) {
    document.body.appendChild(tempDiv)
  }

  // First, check for any section-specific theme markers
  // We'll only process elements that aren't inside a section with data-theme-processed='true'
  const processedSections = tempDiv.querySelectorAll('.canvas-section[data-theme-processed="true"]')
  const elementsToSkip = new Set<Element>()

  // Collect all elements inside processed sections to skip them
  processedSections.forEach((section) => {
    const sectionChildren = Array.from(section.querySelectorAll('*'))
    sectionChildren.forEach((el) => elementsToSkip.add(el))
  })

  // Also skip any elements that have been directly styled by section processing
  tempDiv.querySelectorAll('[data-section-styled]').forEach((el) => {
    elementsToSkip.add(el)
  })

  const headingLevels = [1, 2, 3] as const
  for (const level of headingLevels) {
    const headingClass = `c-theme-preset--h_${level}`

    processElements(tempDiv, `.${headingClass}`, (element) => {
      // Skip if this element is inside a section with processed theme
      if (elementsToSkip.has(element)) {
        return
      }

      const styles = headingStyling[level]
      element.classList.remove(headingClass)
      element.classList.remove('c-theme-preset')

      const isInSection = element.closest('.canvas-section') !== null
      const sectionElement = element.closest('.canvas-section') as HTMLElement
      const shouldUseParentTheme = sectionElement && sectionElement.getAttribute('data-theme-inherited') === 'true'

      // Use section theme for sections with inherit_theme_from_parent=true, otherwise use global theme
      const prefix = isInSection && !shouldUseParentTheme ? '--section' : '--theme'

      // Mark this as processed by global theme
      element.setAttribute('data-processed-by-global', 'true')

      applyThemeStyles(
        element,
        `h_${level}`,
        prefix,
        {
          fontWeight: level <= 2 ? '300' : '400',
          fontSize: `${styles.fontSize}px`,
        },
        false,
        shouldPreserveStyles,
      )
    })
  }

  const paragraphTypes = [
    { class: 'c-theme-preset--big_text', type: Paragraphs.BIG_TEXT },
    { class: 'c-theme-preset--normal_text', type: Paragraphs.NORMAL_TEXT },
    { class: 'c-theme-preset--small_text', type: Paragraphs.SMALL_TEXT },
  ]

  paragraphTypes.forEach(({ class: className, type }) => {
    processElements(tempDiv, `.${className}`, (element) => {
      // Skip if this element is inside a section with processed theme
      if (elementsToSkip.has(element)) {
        return
      }

      const styles = paragraphStyling[type]
      element.classList.remove(className)
      element.classList.remove('c-theme-preset')

      const isInSection = element.closest('.canvas-section') !== null
      const sectionElement = element.closest('.canvas-section') as HTMLElement
      const shouldUseParentTheme = sectionElement && sectionElement.getAttribute('data-theme-inherited') === 'true'

      // Use section theme for sections with inherit_theme_from_parent=true, otherwise use global theme
      const prefix = isInSection && !shouldUseParentTheme ? '--section' : '--theme'
      const varName = paragraphTypeToVarName[type]

      // Mark this as processed by global theme
      element.setAttribute('data-processed-by-global', 'true')

      applyThemeStyles(
        element,
        varName,
        prefix,
        {
          fontWeight: '400',
          fontSize: `${styles.fontSize}px`,
        },
        true,
        shouldPreserveStyles,
      )
    })
  })

  if (!isInDocument) {
    document.body.removeChild(tempDiv)
  }

  return tempDiv.innerHTML
}

function applyThemeStyles(
  element: HTMLElement,
  varName: string,
  prefix: string,
  defaults: { fontWeight: string; fontSize: string },
  processSpans = false,
  shouldPreserveStyles = false,
): void {
  // Skip elements that already have theme styles directly applied (by section processing)
  if (element.hasAttribute(`data-theme-${varName}-applied`)) {
    return
  }

  const computedStyle = window.getComputedStyle(element)

  // Define variable names
  const fontFamilyVar = `${prefix}-${varName}-font-family`
  const colorVar = `${prefix}-${varName}-color`
  const fontWeightVar = `${prefix}-${varName}-font-weight`
  const fontSizeVar = `${prefix}-${varName}-font-size`
  const letterSpacingVar = `${prefix}-${varName}-letter-spacing`
  const lineHeightVar = `${prefix}-${varName}-line-height`
  const textTransformVar = `${prefix}-${varName}-text-transform`

  // Get computed values with fallbacks
  const fontFamily = getComputedVarValue(computedStyle, fontFamilyVar) || computedStyle.fontFamily || 'inherit'
  const color = getComputedVarValue(computedStyle, colorVar) || computedStyle.color || 'inherit'
  const fontWeight = getComputedVarValue(computedStyle, fontWeightVar) || defaults.fontWeight
  const fontSize = getComputedVarValue(computedStyle, fontSizeVar) || defaults.fontSize
  const letterSpacing =
    getComputedVarValue(computedStyle, letterSpacingVar) ||
    (computedStyle.letterSpacing !== 'normal' ? computedStyle.letterSpacing : 'normal')
  const lineHeight = getComputedVarValue(computedStyle, lineHeightVar) || computedStyle.lineHeight || '120%'
  const textTransform =
    getComputedVarValue(computedStyle, textTransformVar) ||
    (computedStyle.textTransform !== 'none' ? computedStyle.textTransform : 'none')

  // Apply base styles
  Object.assign(element.style, {
    fontFamily,
    color,
    fontWeight,
    fontSize,
    lineHeight,
  })

  // Apply conditional styles
  if (letterSpacing !== 'normal') element.style.letterSpacing = letterSpacing
  if (textTransform !== 'none') element.style.textTransform = textTransform

  // Process spans if needed
  if (processSpans) {
    element.querySelectorAll('span').forEach((span) => {
      // Skip spans that are already processed
      if (span.hasAttribute(`data-theme-${varName}-applied`)) {
        return
      }

      const spanEl = span as HTMLElement

      if (shouldPreserveStyles) {
        // Apply styles only if they don't already exist on the span
        if (!spanEl.style.lineHeight) spanEl.style.lineHeight = lineHeight
        if (!spanEl.style.fontWeight) spanEl.style.fontWeight = fontWeight
        if (!spanEl.style.fontSize) spanEl.style.fontSize = fontSize
        if (!spanEl.style.color) spanEl.style.color = color
        if (letterSpacing !== 'normal' && !spanEl.style.letterSpacing) spanEl.style.letterSpacing = letterSpacing
        if (textTransform !== 'none' && !spanEl.style.textTransform) spanEl.style.textTransform = textTransform
      } else {
        // Original behavior - apply all styles
        Object.assign(spanEl.style, {
          lineHeight,
          fontWeight,
          fontSize,
          color,
        })

        if (letterSpacing !== 'normal') spanEl.style.letterSpacing = letterSpacing
        if (textTransform !== 'none') spanEl.style.textTransform = textTransform
      }
    })
  }

  // Special handling for paragraph elements
  if (element.tagName === 'P' || varName.includes('_text')) {
    // Check for any nested block elements that need styling
    element.querySelectorAll('div, p, li').forEach((nestedEl) => {
      const nestedElem = nestedEl as HTMLElement

      // Skip elements that are already processed
      if (nestedElem.hasAttribute(`data-theme-${varName}-applied`)) {
        return
      }

      Object.assign(nestedElem.style, {
        fontFamily,
        color,
        fontWeight,
        fontSize,
        lineHeight,
      })

      if (letterSpacing !== 'normal') nestedElem.style.letterSpacing = letterSpacing
      if (textTransform !== 'none') nestedElem.style.textTransform = textTransform

      // Mark as processed
      nestedElem.setAttribute(`data-theme-${varName}-applied`, 'true')
    })
  }

  // Mark as processed to avoid duplicate processing
  element.setAttribute(`data-theme-${varName}-applied`, 'true')
}

function getComputedVarValue(computedStyle: CSSStyleDeclaration, variableName: string): string {
  const value = computedStyle.getPropertyValue(variableName).trim()
  return value || ''
}

function processElements(container: HTMLElement, selector: string, callback: (element: HTMLElement) => void): void {
  const elements = container.querySelectorAll(selector)
  elements.forEach((element) => callback(element as HTMLElement))
}

/**
 * Converts a measurement value to pixels.
 * If the value is a number, it's assumed to be in pixels.
 * If it's a string, it must include a Gotenberg-supported unit (mm, cm, in, px, pt, pc).
 * Supports both comma and period as decimal separators (e.g., "10.5mm" or "10,5mm").
 *
 * @param value - Number in pixels or string with unit (e.g., "1000mm", "100,5cm", "4.5in", "96px", "72pt", "6pc")
 * @returns Value in pixels
 */
export function convertToPixels(value: number | string | undefined): number | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  if (typeof value === 'number') {
    return value
  }

  // Check if it's a plain number string without unit (treat as pixels)
  const plainNumberMatch = value.match(/^(\d+(?:[.,]\d+)?)$/)
  if (plainNumberMatch) {
    return parseFloat(plainNumberMatch[1].replace(',', '.'))
  }

  // Accept both comma and period as decimal separators
  const match = value.match(new RegExp(`^(\\d+(?:[.,]\\d+)?)${GOTENBERG_DIMENSION_PATTERN}$`))
  if (!match) {
    console.warn(
      `Invalid measurement format: ${value}. Expected format: number with unit (e.g., "1000mm", "100cm", "4in", "96px", "72pt", "6pc") or plain number for pixels (e.g., "1280")`,
    )
    return undefined
  }

  // Normalize comma to period for parseFloat
  const numericValue = parseFloat(match[1].replace(',', '.'))
  const unit = match[2] as GotenbergDimensionUnit

  const conversionFactor = DIMENSION_TO_PIXEL_CONVERSION[unit]
  if (conversionFactor === undefined) {
    console.warn(`Unsupported unit: ${unit}`)
    return undefined
  }

  return numericValue * conversionFactor
}

/**
 * Formats a dimension value for Gotenberg PDF conversion.
 * If the value is a string with Gotenberg-supported units (mm, cm, in, px, pt, pc), normalizes it to use period as decimal separator.
 * If it's a number, appends "px" since the convention is that numbers represent pixels.
 * Supports both comma and period as decimal separators (e.g., "10.5mm" or "10,5mm").
 *
 * Gotenberg supports: 72pt, 96px, 1in, 25.4mm, 2.54cm, or 6pc. Default unit is inches if unspecified.
 *
 * @param value - Number in pixels or string with unit (e.g., "1000mm", "100,5cm", "4.5in", "96px", "72pt", "6pc")
 * @returns Formatted value for Gotenberg (e.g., "1000mm", "10.5cm", "4in", "96px", "72pt", "6pc") or undefined
 */
export function formatDimensionForGotenberg(value: number | string | undefined): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }

  // If it's already a string with units, validate and normalize
  if (typeof value === 'string') {
    // Check if it's a plain number string without unit (treat as pixels)
    const plainNumberMatch = value.match(/^(\d+(?:[.,]\d+)?)$/)
    if (plainNumberMatch) {
      const normalizedNumber = plainNumberMatch[1].replace(',', '.')
      return `${normalizedNumber}px`
    }

    // Accept both comma and period as decimal separators
    const match = value.match(new RegExp(`^(\\d+(?:[.,]\\d+)?)${GOTENBERG_DIMENSION_PATTERN}$`))
    if (!match) {
      console.warn(
        `Invalid measurement format: ${value}. Expected format: number with unit (e.g., "1000mm", "100cm", "4in", "96px", "72pt", "6pc") or plain number for pixels (e.g., "1280")`,
      )
      return undefined
    }
    // Normalize comma to period for Gotenberg (which expects period)
    const normalizedNumber = match[1].replace(',', '.')
    const unit = match[2]
    return `${normalizedNumber}${unit}`
  }

  // If it's a number (pixels), append "px" unit
  if (typeof value === 'number') {
    return `${value}px`
  }

  return undefined
}
